<script setup lang="ts">
import { ref, computed, onMounted, watch } from "vue";
import { useBapi } from "@/bapi-client";
import NoteWriterSelector from "@/components/app/NoteWriter";
import { FilePreviewModal, ScrollActionList, CommentCard } from "@/components/common";
import { BAPI_COMMANDS } from "@/bapi-client/types/commands";
import {
  HandlerTicketNotesResponse,
  BAPITicketNoteAttachment,
  BAPICustomerSupportTicket,
} from "@/bapi-client/types/customer-support";
import { capitalize } from "@/utils/text";
import PopoverTag from "@/components/common/PopoverTag.vue";
import { Button } from "@/components/ui/button";
import TicketDetailsAssetRow from "./TicketDetailsAssetRow.vue";
import TicketDetailsAssignee from "./TicketDetailsAssignee.vue";
import CloseTicket from "./CloseTicket.vue";
import { useAttachments } from "@/composables/attachments";
import useTrackerStore from "@/stores/trackers";
import { RUDDERSTACK_EVENTS } from "@/lib/rudderstack";
import { CSCreateNoteEvent } from "@/types/rudderstack";
import { useRouter } from "vue-router";
import { toast } from "vue-sonner";

const { attachments, thumbnails, loadThumbnails, files, downloadFile, previewFile } = useAttachments();

interface Props {
  customerId: string;
  ticketId: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: "assigned"): void;
}>();
const trackers = useTrackerStore();
const router = useRouter();

const title = defineModel<string>("title");

const ticket = ref<BAPICustomerSupportTicket>();
const ticketNotes = ref<HandlerTicketNotesResponse>();

const imagePreview = ref<{ fileId: string; noteId: string; fileName: string; fileType: string; src: string }>();
const showPreview = ref(false);
const isSaving = ref(false);
const isSaved = ref(false);
const failedToSave = ref(false);

interface NoteForm {
  text?: string;
  attachments?: FileList;
}

async function preview(noteId: string, fileId: string) {
  const file = attachments.value[noteId]?.find((att) => att.id === fileId);
  if (!file) return undefined;

  toast.loading("Loading preview");
  const srcURL = await previewFile(noteId, fileId);
  toast.dismiss();
  if (!srcURL) return undefined;
  imagePreview.value = {
    noteId,
    fileId,
    fileName: file.filename,
    fileType: file.mime_type,
    src: srcURL,
  };
  showPreview.value = true;
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_VIEW_ATTACHMENT, {
    attachment_name: file.filename,
  });
}

async function downloadAttachment(noteId: string, fileId: string) {
  const result = await downloadFile(noteId, fileId);
  if (!result) {
    return trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_DOWNLOAD_ATTACHMENT, {
      success: false,
    });
  }
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_DOWNLOAD_ATTACHMENT, { success: true });
}

async function submitNote(newNote: NoteForm): Promise<void> {
  failedToSave.value = false;
  isSaving.value = true;
  isSaved.value = false;
  if (!newNote.text && !newNote.attachments) {
    toast.warning("Note cannot be empty!");
    isSaving.value = false;
    isSaved.value = false;
    return;
  }
  if (!ticket.value?.customer_id || !ticket.value?.id || !ticket.value?.id_prefix) {
    toast.warning("Ticket information is missing!");
    isSaving.value = false;
    isSaved.value = false;
    failedToSave.value = true;
    return;
  }
  const rsData: CSCreateNoteEvent = {
    ticket_id: props.ticketId,
    has_text: newNote.text ? true : false,
    attachment_count: newNote.attachments ? newNote.attachments.length : 0,
    success: false,
  };
  let noteAttachments: BAPITicketNoteAttachment[] = [];
  if (newNote.attachments) {
    const attachmentPromises = Array.from(newNote.attachments).map(async (attachment) => {
      const result = await useBapi(
        BAPI_COMMANDS.CUSTOMER_SUPPORT_GET_TICKET_NOTE_SIGNED_UPLOAD_URL,
        ticket.value!.customer_id,
      );
      if (result.success) {
        const uploadResponse = await useBapi(BAPI_COMMANDS.S3_UPLOAD_FILE, attachment, result.data.uploadUrl);
        if (!uploadResponse.success) {
          console.error("Failed to upload file, received ", uploadResponse.error.status);
          toast.warning(`Could not save attachment: ${attachment.name}`);
          return null;
        }
        return {
          attachment_id: result.data.attachmentId,
          filename: attachment.name,
          mime_type: attachment.type,
          filesize: attachment.size,
          alt_text: attachment.name,
        };
      } else {
        console.error("Failed to get signed upload url for attachment");
        toast.warning(`Could not save attachment: ${attachment.name}`);
        rsData.error = result.error;
        trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_CREATE_NOTE, rsData);
        return null;
      }
    });
    const uploadResults = await Promise.all(attachmentPromises);
    for (const res of uploadResults) {
      if (res !== null) {
        noteAttachments.push(res);
      }
    }
  }

  const ticketNotesResult = await useBapi(BAPI_COMMANDS.CUSTOMER_SUPPORT_CREATE_TICKET_NOTE, {
    customer_id: ticket.value?.customer_id,
    ticket_id: ticket.value?.id,
    ticket_prefix: ticket.value?.id_prefix,
    note: newNote.text ?? "",
    attachments: noteAttachments,
  });

  if (ticketNotesResult.success) {
    toast.success("Ticket comment created!");
    isSaved.value = true;
    ticketNotes.value = ticketNotesResult.data;
    for (const note of ticketNotes.value) {
      if (!attachments.value[note.id]) {
        attachments.value[note.id] = note.attachments ?? [];
      }
    }
    isSaved.value = true;
    rsData.success = true;
    trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_CREATE_NOTE, rsData);
  }

  isSaving.value = false;
}

const priorityIconClass = computed(() => {
  if (ticket.value?.priority.toLowerCase() === "high") return "text-red-400";
  if (ticket.value?.priority.toLowerCase() === "medium") return "text-orange-400";
  return "text-yellow-400";
});

function closePreview() {
  imagePreview.value = undefined;
  showPreview.value = false;
}

const loadedEmpty = computed(() => {
  return ticket.value?.loaded_empty === "L" ? "Loaded" : ticket.value?.loaded_empty === "E" ? "Empty" : "- -";
});

const railroad = computed(() => {
  if (ticket.value?.railroads.length && ticket.value?.railroads.length > 0) return ticket.value?.railroads[0];
  return "- -";
});

const assignee = computed(() => {
  if (ticket.value?.assignees.length && ticket.value?.assignees.length > 0) {
    return `${ticket.value?.assignees[0].first_name} ${ticket.value?.assignees[0].last_name}`;
  }
  return "- -";
});

const firstTag = computed(() => {
  if (ticket.value?.tags?.length && ticket.value?.tags.length > 0) {
    return ticket.value?.tags[0];
  }
  return "- -";
});

const moreTags = computed(() => {
  if (ticket.value?.tags?.length && ticket.value?.tags.length > 1) {
    return ticket.value?.tags.slice(1);
  }
  return undefined;
});

async function getTicket() {
  const ticketResult = await useBapi(BAPI_COMMANDS.CUSTOMER_SUPPORT_GET_TICKET, props.customerId, props.ticketId);
  if (ticketResult.success) {
    ticket.value = ticketResult.data;
    title.value = ticket.value.name;
  }
}

async function getTicketNotes() {
  const ticketNotesResult = await useBapi(
    BAPI_COMMANDS.CUSTOMER_SUPPORT_GET_TICKET_NOTES,
    props.customerId,
    props.ticketId,
  );
  if (ticketNotesResult.success) {
    ticketNotes.value = ticketNotesResult.data;
    for (const note of ticketNotes.value) {
      if (!attachments.value[note.id]) {
        attachments.value[note.id] = note.attachments ?? [];
      }
    }
  }
}

async function closeTicket() {
  const result = await useBapi(BAPI_COMMANDS.CUSTOMER_SUPPORT_UPDATE_TICKET, {
    customer_id: props.customerId,
    ticket_id: props.ticketId,
    state: "Closed",
  });
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_CLOSE, { success: result.success });
  if (!result.success) {
    toast.warning(`Unable to close ticket.`);
  } else {
    toast.success(`TICKET-${props.ticketId} closed.`);
    router.push({
      name: "customerSupport",
      params: { customerId: props.customerId },
    });
  }
}

function trackAssetClickThrough(asset_id: string, waybill_id: string) {
  console.log(asset_id, waybill_id);
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_OPEN_ASSET_DETAILS, {
    asset_id,
    waybill_id,
  });
}

function assignTicket() {
  getTicket();
  emit("assigned");
}

onMounted(() => {
  getTicket();
  getTicketNotes();
});

watch(
  () => props.ticketId,
  () => {
    getTicket();
    getTicketNotes();
  },
);
</script>

<template>
  <div>
    <div class="border-border flex gap-3 border-b py-4">
      <span class="!mb-0 w-[12.5%] self-center text-sm font-semibold !text-gray-500">Status</span>
      {{ ticket?.state && capitalize(ticket.state) }}
    </div>
    <div class="border-border grid grid-cols-2 grid-rows-3 gap-3 border-b py-4">
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Priority</span>
        <span>
          <i class="fa-solid fa-fire-flame-simple mr-1 text-lg" :class="priorityIconClass" />{{
            ticket?.priority && capitalize(ticket.priority)
          }}
        </span>
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Creator</span>
        {{ ticket?.created_by.first_name }} {{ ticket?.created_by.last_name }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">L/E</span>
        {{ loadedEmpty }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Ticket #</span>
        {{ ticket?.id }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Railroad</span>
        {{ railroad }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Date</span>
        {{ ticket?.created_ts ? $date("MM/DD/YYYY", ticket?.created_ts) : "" }}
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Tags</span>
        {{ firstTag }}
        <PopoverTag v-if="moreTags" name="tags" :label="`+ ${moreTags.length}`">
          <ul class="divide-y">
            <template v-for="tag in moreTags" :key="tag">
              <li class="px-3 py-1.5 text-sm font-semibold">{{ tag }}</li>
            </template>
          </ul>
        </PopoverTag>
      </div>
      <div class="flex items-center gap-3">
        <span class="!mb-0 w-1/4 text-sm font-semibold !text-gray-500">Assignee</span>
        {{ assignee }}
      </div>
    </div>
    <div v-if="ticket?.ticket_assets?.length ?? 0 > 0" class="border-border flex flex-col gap-1.5 border-b py-3">
      <TicketDetailsAssetRow
        v-for="asset in ticket?.ticket_assets"
        :key="asset.equipment_id"
        :asset="asset"
        :customer-id="customerId"
        @open-asset="trackAssetClickThrough"
      />
    </div>
    <div v-if="ticket?.description" class="border-border flex gap-3 border-b py-4">
      {{ ticket?.description }}
    </div>
    <div
      v-if="ticket && ticket.state !== 'Closed'"
      class="border-border flex w-full items-center justify-between gap-3 border-b py-3"
    >
      <router-link :to="{ name: 'editTicket', params: { ticketId } }">
        <Button
          variant="secondary"
          @click="trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKET_START_EDIT, {})"
        >
          Edit ticket
        </Button>
      </router-link>
      <TicketDetailsAssignee
        :ticket="ticket"
        :class="{
          'ml-auto': !ticket.assignees.length,
          'mr-auto': ticket.assignees.length,
        }"
        @assigned="assignTicket"
      />
      <CloseTicket :ticket-name="ticket?.name" @close-ticket="closeTicket" />
    </div>
    <div v-if="ticketNotes && ticketNotes.length > 0" class="border-border flex gap-3 border-b py-4">
      <FilePreviewModal
        v-if="imagePreview"
        :is-active="showPreview"
        :file-type="imagePreview?.fileType"
        :file-name="imagePreview?.fileName"
        :url="imagePreview?.src"
        @download="downloadAttachment(imagePreview?.noteId, imagePreview.fileId)"
        @dismiss="closePreview"
      />
      <ScrollActionList class="pr-2" :show-divider="false" :scroll-down="false" @visible="loadThumbnails">
        <CommentCard
          v-for="note in ticketNotes ?? []"
          :key="note.id"
          :note="{
            id: note.id,
            waybill_id: '',
            equipment_id: '',
            user_id: note.user.user_id,
            note_text: note.note,
            created_ts: note.createdTs,
            updated_ts: '',
            customer_id: '',
            user_name: note.user.first_name + ' ' + note.user.last_name,
            user_initials: note.user.first_name[0] + note.user.last_name[0],
            company_name: '',
            attachments: note.attachments || [],
            signed_profile_pictures: note.user.profile_picture,
          }"
          :thumbnails="thumbnails[note.id]"
          :is-clickable="false"
          :files="files[note.id]"
          :show-attachments="true"
          @download-file="downloadFile(note.id, $event)"
          @preview-file="preview(note.id, $event)"
        />
      </ScrollActionList>
    </div>
    <div class="flex gap-3 py-4">
      <NoteWriterSelector
        use-legacy-note-writer
        :ticket-id="ticket?.qualified_id ?? ''"
        :is-saving="isSaving"
        :is-saved="isSaved"
        @submit-note="submitNote"
      />
    </div>
  </div>
</template>
